import { Link } from "react-router-dom";
import { ArrowRight, Quote } from "lucide-react";
import { BrandReviews } from "../components/BrandReviews";
import { usePageMeta } from "../hooks/usePageMeta";

/**
 * Dedicated Reviews page — DB-backed reviews rendered in our own brand style
 * for reliability and visual consistency.
 */
export default function ReviewsPage() {
  usePageMeta({
    path: "/reviews",
    title: "Reviews , What MediTalk participants say · MediTalk",
    description:
      "Verified reviews from patients and caregivers who've taken part in MediTalk healthcare research studies. Real stories, real impact.",
  });
  return (
    <div>
      {/* Hero — DARK */}
      <section className="bg-[var(--dark-bg)] grain-dark relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-[var(--lime)]/10 blur-3xl pointer-events-none" />

        <div className="container-main py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-[var(--lime)] mb-3">
                <span className="w-8 h-px bg-[var(--lime)]" />
                Real voices
              </span>
              <h1 className="font-['Outfit'] font-bold text-white tracking-tight text-4xl md:text-5xl lg:text-6xl mb-4 leading-[1.05]">
                What patients &amp; caregivers
                <span className="text-[var(--lime)]"> say about us.</span>
              </h1>
              <p className="text-lg text-[var(--gray-light)] leading-relaxed max-w-2xl">
                Real, unedited words from patients and caregivers who have
                taken part in our interviews, surveys and bulletin boards.
              </p>
            </div>

            {/* Brand-styled badge */}
            <div className="lg:col-span-4 flex lg:justify-end">
              <BrandReviews mode="badge" />
            </div>
          </div>
        </div>
      </section>

      {/* Reviews wall — WHITE */}
      <section className="bg-white" data-testid="reviews-wall">
        <div className="container-main py-6 md:py-8">
          <BrandReviews mode="wall" />
        </div>
      </section>

      {/* CTA — DARK */}
      <section className="bg-[var(--dark-bg)] grain-dark">
        <div className="container-main py-16 md:py-20 text-center">
          <Quote
            size={36}
            className="text-[var(--lime)] mx-auto mb-5"
            strokeWidth={1.5}
          />
          <h2 className="font-['Outfit'] font-bold text-white tracking-tight text-3xl md:text-4xl lg:text-5xl mb-4 max-w-2xl mx-auto leading-tight">
            Want to add your voice?
          </h2>
          <p className="text-[var(--gray-light)] text-lg max-w-xl mx-auto mb-8 leading-relaxed">
            Join free and take part in research that fits your life: interviews,
            surveys or online bulletin boards.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/patient/sign-up"
              className="btn-primary"
              data-testid="reviews-cta-patient"
            >
              Join as patient
              <ArrowRight size={18} />
            </Link>
            <Link
              to="/caregiver/sign-up"
              className="btn-secondary"
              data-testid="reviews-cta-caregiver"
            >
              Join as caregiver
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
